import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as familiesApi from '@/api/families';
import type { FamilyFormData, FamilyFilters, VisitFormData } from '@/types/family';

const FAMILIES_KEY = ['families'] as const;

export function useFamilies(filters?: FamilyFilters) {
  return useQuery({
    queryKey: [...FAMILIES_KEY, 'list', filters],
    queryFn: () => familiesApi.getFamilies(filters),
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useFamily(id: string | undefined) {
  return useQuery({
    queryKey: [...FAMILIES_KEY, 'detail', id],
    queryFn: () => familiesApi.getFamily(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FamilyFormData) => familiesApi.createFamily(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILIES_KEY });
    },
  });
}

export function useUpdateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<FamilyFormData>;
    }) => familiesApi.updateFamily(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...FAMILIES_KEY, 'detail', variables.id],
      });
      void queryClient.invalidateQueries({
        queryKey: [...FAMILIES_KEY, 'list'],
      });
    },
  });
}

export function useDeleteFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => familiesApi.deleteFamily(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILIES_KEY });
    },
  });
}

export function useFamilyVisits(familyId: string | undefined) {
  return useQuery({
    queryKey: [...FAMILIES_KEY, 'visits', familyId],
    queryFn: () => familiesApi.getFamilyVisits(familyId!),
    enabled: !!familyId,
  });
}

export function useCreateVisit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VisitFormData) => familiesApi.createVisit(data),
    onSuccess: (_data, variables) => {
      if (variables.family_id) {
        void queryClient.invalidateQueries({
          queryKey: [...FAMILIES_KEY, 'visits', variables.family_id],
        });
      }
      // Also invalidate general visits list (for member-level visits)
      void queryClient.invalidateQueries({ queryKey: ['visits', 'list'] });
    },
  });
}
